import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../library/components/d-popup.js';
import '../library/editors/elements/d-checkbox.js';
import '../library/editors/elements/d-edit-textarea.js';
import '../library/elements/d-section.js';
import '../library/fields/d-view-info.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { t } from 'src/i18n';
import type { AlertsOnChangeInput } from './alerts-on-change-input';
import type { AlertsOnChangeResult } from './alerts-on-change-result';
import type { DialogActionInput } from '../library/components/DialogActionInput';

/**
 *
 * USAGE:
 *    base-event-editor
 *    d-task-view
 *
 */
@customElement('alerts-on-change-dialog')
export class AlertsOnChangeDialog extends BaseDialog<AlertsOnChangeInput, AlertsOnChangeResult> {
  @property({ type: Boolean })
  show = false;
  /**
   * Changes the title depending on the type of event
   */
  @property({ type: Boolean })
  taskEvent = false;
  /**
   * Set to true if there are no eligible recipients. There should be a noAccessMessage included.
   */
  @property({ type: Boolean })
  alertsDisabled = false;
  /**
   * Allow user to include all details
   */
  @property({ type: Boolean })
  noDetails = false;
  @property({ type: Boolean })
  includeDetails = false;
  @property({ type: String })
  message = '';
  @property({ type: String })
  noAccessMessage = '';
  @property({ type: Boolean })
  newItem = false;
  @property({ type: Boolean })
  isDelete = false;
  width = 500;
  private get includeDetailsLabel() {
    return t('content.alertsIncludeDetails');
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('content.cancel'), action: 'cancel' },
      { name: t('content.alertsDoNotSend'), action: 'save' },
      { name: t('content.alertsSend'), action: 'save-and-alert', main: true, disabled: this.alertsDisabled },
    ];
  }

  protected get calculatedTitle(): string {
    const type = this.taskEvent ? t('content.alertsTypeRoutine') : t('content.alertsTypeTask');
    let changeType = t('content.alertsChangeTypeChanged');
    if (this.newItem) {
      changeType = t('content.alertsChangeTypeNew');
    }
    if (this.isDelete) {
      changeType = t('content.alertsChangeTypeDeleted');
    }

    return t('content.alertsTitle', { changeType, type });
  }

  private get info() {
    if (this.taskEvent) {
      return t('content.alertsInfoRoutine');
    }
    return t('content.alertsInfoTask');
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${this.info}></d-view-info>
      </d-section>
      <d-section topless>
        <d-edit-textarea
          placeholder=${t('content.commentPlaceholder')}
          value=${this.message}
          @value-changed=${(e) => (this.message = e.detail.value)}
        ></d-edit-textarea>
      </d-section>
      ${this.noDetails
        ? nothing
        : html`
            <d-section topless
              ><d-checkbox
                ?checked=${this.includeDetails}
                .optionLabel=${this.includeDetailsLabel}
                @checked-changed=${(e) => (this.includeDetails = e.detail.checked)}
              ></d-checkbox>
            </d-section>
          `}
      ${!this.noAccessMessage
        ? nothing
        : html`
            <d-section>
              <d-view-info .content=${this.noAccessMessage}></d-view-info>
            </d-section>
          `}
    `;
  }

  protected fetchResult(detail: string | undefined): AlertsOnChangeResult {
    if (detail === 'save-and-alert') {
      return {
        action: 'save-and-alert',
        includeDetails: this.includeDetails,
        message: this.message,
      };
    } else if (detail === 'save') {
      return {
        action: 'save',
      };
    } else {
      return {
        action: 'cancel',
      };
    }
  }

  protected initializeDialog(input: AlertsOnChangeInput) {
    this.taskEvent = input.taskEvent;
    this.alertsDisabled = input.alertsDisabled;
    this.noDetails = input.noDetails;
    this.noAccessMessage = input.noAccessMessage;
    this.newItem = input.newItem;
    this.isDelete = input.isDelete;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'alerts-on-change-dialog': AlertsOnChangeDialog;
  }
}
